// Notification preference categories and their role scoping.
//
// Each category mirrors a notify(...) `category` on the backend. The UI is
// role-scoped (per the recipient matrix in the approved plan): a user only sees
// the categories that can actually target one of their roles.
//
// SINGLE SOURCE OF TRUTH: the backend is authoritative for WHICH categories a
// role may toggle. The GET /users/notification-preferences/ response already
// returns exactly the role-allowed categories (one entry per category, with a
// `label`), so the settings UI renders from that response. This config is the
// OFFLINE FALLBACK only (used when the endpoint fails) and the source of the
// longer `description` helper text the backend does not provide.
//
// The `roles` matrix below MUST stay in lock-step with the backend
// ROLE_NOTIFICATION_CATEGORIES map in users/views.py. That sentence used to be
// a promise nobody could check, and it drifted twice: the mandate grants for
// Coach/Staff/AgencyAdmin (BE #220, 2026-07-30) and the whole
// `coaching_personality` category (BE #247) landed after the last hand-sync,
// so an offline user saw a category list the backend had already outgrown.
// `notificationCategories.lockstep.test.js` now reads the backend map out of
// users/views.py whenever a backend checkout is reachable and fails on drift.
// Current union:
//   Agent       -> tenders, pitches, messages, meetings, mandate, positions, invites, scouting, transfer_windows, agency
//   AgencyAdmin -> tenders, pitches, messages, meetings, mandate, positions, invites, scouting, transfer_windows, agency
//   ClubAdmin   -> tenders, pitches, messages, meetings, positions, invites, scouting, coaching_personality
//   Staff       -> tenders, pitches, messages, meetings, mandate, positions, invites, scouting, coaching_personality
//   Coach       -> tenders, pitches, messages, meetings, mandate, positions, invites, scouting, coaching_personality
//   Player      -> messages, meetings, mandate, invites
//   Lawyer      -> messages, meetings, pitches, tenders (lawyers pitch via D-011,
//                  so tender_closed reaches them too - BE #250)
//   Organizer   -> messages, meetings
// (Organizer needs no entry below: `messages` and `meetings` use roles: null
// = every role.)
//
// `key`         -> the category sent to /users/notification-preferences/
// `label`       -> user-facing tab label (no em/en-dashes)
// `description` -> short helper text
// `roles`       -> roles for which this category is relevant. A user sees the
//                  category if ANY of their roles is in this list. `null` means
//                  "every role" (channels everyone can receive).
import { ROLES } from "./roles";

export const NOTIFICATION_CATEGORIES = [
  {
    key: "tenders",
    label: "Requests",
    description:
      "New requests and pin status, and activity reminders for your club's own requests.",
    // Lawyer: PRESENT since BE #250. The note that used to stand here said the
    // switch was owed and that this file must not run ahead of the backend;
    // that grant has now landed, and the lock-step test went red on this exact
    // line the moment it did, which is what it was built for.
    // `tender_closed` (category 'tenders', WITH an email payload) targets every
    // creator of a still-open pitch, and a lawyer is one of them on staff
    // requests (D-011), on all three end-of-life paths.
    roles: [
      ROLES.Agent,
      ROLES.AgencyAdmin,
      ROLES.ClubAdmin,
      ROLES.Staff,
      ROLES.Coach,
      ROLES.Lawyer,
    ],
  },
  {
    key: "pitches",
    label: "Pitches",
    description:
      "Incoming pitches on your requests, and the outcome of pitches you sent.",
    // Lawyer: applies via the regular pitch-create endpoint (D-011), so the
    // pitch_response_* outcome events target lawyers under this category
    // (BE grant on the category-registration PR).
    roles: [
      ROLES.ClubAdmin,
      ROLES.Staff,
      ROLES.Coach,
      ROLES.Agent,
      ROLES.AgencyAdmin,
      ROLES.Lawyer,
    ],
  },
  {
    key: "messages",
    label: "Messages",
    description: "Direct chat messages.",
    roles: null, // every role can receive chat messages
  },
  {
    key: "meetings",
    label: "Meetings",
    description: "Meetings created, changed or cancelled.",
    roles: null, // every role can host or be a guest
  },
  {
    key: "mandate",
    label: "Representations",
    description: "Representation invitations, player responses and expiry reminders.",
    // Coach, Staff and AgencyAdmin were granted 'mandate' on the backend (BE
    // #220) because the staff-mandate lane mails all three: the invited
    // subject is resolved from (mandate.coach, mandate.staff) and the mandate
    // owner is the caller's own Agent profile, which an AgencyAdmin has.
    // ClubAdmin is deliberately absent, see users/views.py.
    roles: [
      ROLES.Agent,
      ROLES.AgencyAdmin,
      ROLES.Staff,
      ROLES.Coach,
      ROLES.Player,
    ],
  },
  {
    key: "positions",
    label: "Position updates",
    description: "Player position changes by an agent or a club.",
    roles: [
      ROLES.ClubAdmin,
      ROLES.Staff,
      ROLES.Coach,
      ROLES.Agent,
      ROLES.AgencyAdmin,
    ],
  },
  {
    key: "invites",
    label: "Invitations",
    // Invitee-side events (invitation_received/declined) landed with the V2
    // wave (BE #145), so invitee roles are granted too. The server
    // description still wins when online (see getCategoriesFromServer).
    description:
      "Invitation activity: when someone you invited accepts or declines, or when you are invited to join.",
    roles: [
      ROLES.AgencyAdmin,
      ROLES.ClubAdmin,
      ROLES.Agent,
      ROLES.Staff,
      ROLES.Coach,
      ROLES.Player,
    ],
  },
  {
    key: "agency",
    label: "Agency activity",
    description:
      "Agency taskboard activity and players assigned to your agents.",
    roles: [ROLES.AgencyAdmin, ROLES.Agent],
  },
  {
    key: "scouting",
    label: "Scouting",
    description:
      "Scouting activity: recommendations on your scout requests, radar updates, and Last Minute listing expiry.",
    roles: [
      ROLES.ClubAdmin,
      ROLES.Staff,
      ROLES.Coach,
      ROLES.Agent,
      ROLES.AgencyAdmin,
    ],
  },
  {
    key: "transfer_windows",
    label: "Transfer windows",
    description: "Reminders when a transfer window is about to open.",
    // Agent and AgencyAdmin ONLY since the E-5 audience flip (QA-V2 gate,
    // Marin 2026-08-04: "Audience umdrehen — №6 erreicht AGENTS, nicht
    // Clubs"). The club roles lost the switch in the same backend verdict
    // that removed them from the №6 audience — a switch for an event that
    // can no longer reach its holder would be dishonest (users/views.py
    // ROLE_NOTIFICATION_CATEGORIES).
    roles: [ROLES.Agent, ROLES.AgencyAdmin],
  },
  {
    key: "coaching_personality",
    label: "Coaching Personality",
    // One event, coaching_personality_update_available: the six-month lock on
    // the self-assessment expired and the portrait may be answered again. It
    // reaches only a profile that carries a Coach or a Staff row, which is why
    // Agent, AgencyAdmin, Player, Lawyer and Organizer are absent. A ClubAdmin
    // is present because their signup writes a Staff row (staff_type='admin').
    description:
      "A reminder when your coaching personality can be updated again.",
    roles: [ROLES.ClubAdmin, ROLES.Staff, ROLES.Coach],
  },
];

// Return only the categories relevant to the given set of roles.
// A category with roles === null is relevant to everyone.
// This is the OFFLINE FALLBACK used only when the backend response is missing
// (see getCategoriesFromServer for the authoritative path).
export const getCategoriesForRoles = (roles = []) => {
  const roleList = Array.isArray(roles) ? roles : [];
  return NOTIFICATION_CATEGORIES.filter(
    (category) =>
      category.roles === null ||
      category.roles.some((role) => roleList.includes(role)),
  );
};

// Lookup of the local config by category key (for label/description enrichment).
const CATEGORY_BY_KEY = NOTIFICATION_CATEGORIES.reduce((acc, c) => {
  acc[c.key] = c;
  return acc;
}, {});

// Build the rendered category list from the backend preferences payload. The
// backend is the single source of truth for WHICH categories a role may toggle
// (it returns exactly the role-allowed categories), so the frontend can no
// longer drift from it. We enrich each server category with the longer local
// `description` (and fall back to the server `label` / key when unknown).
//
// `serverPreferences` is the `preferences` array from
// GET /users/notification-preferences/ : [{ category, in_app, email, label }].
// Returns [] when the payload has no usable categories so callers can fall back.
export const getCategoriesFromServer = (serverPreferences) => {
  if (!Array.isArray(serverPreferences)) return [];
  const seen = new Set();
  const out = [];
  serverPreferences.forEach((row) => {
    const key = row && row.category;
    if (!key || seen.has(key)) return;
    seen.add(key);
    const local = CATEGORY_BY_KEY[key];
    out.push({
      key,
      label: row.label || (local && local.label) || key,
      // The server sends a per-category description precisely so the UI can
      // render entirely from the backend response; the local text is only the
      // offline fallback.
      description: row.description || (local && local.description) || "",
    });
  });
  return out;
};
